/**
 * ATL-1445 stage 3, P8 — the barrier that puts migrated v1 entries at the HEAD of outbox v2.
 *
 * Design §2: the v1 → v2 migration copies an organization's domain-W v1 entries "at the head of the
 * queue". The v2 store orders an organization's queue by the `seq` it assigns on append, so "at the
 * head" is achieved by ORDER OF OPERATIONS, not by rewriting positions: the migration must be the first
 * thing that appends to this organization's queue in the session, ahead of any LIVE write (a post-flip
 * write, which may reference a document a migrated pre-flip write creates). This module is how a live
 * append waits for it:
 *
 *  - the migration registers itself SYNCHRONOUSLY, in the same callback that pinned the session
 *    (`kickV1MigrationAfterPin`, `writeCutoverSession.ts`), so no writer can run in between;
 *  - `enqueuePrepared` (`outboxQueue.ts`, the one path a live v2 write takes) awaits
 *    `v1MigrationSettled(org)` before appending;
 *  - the session's v2 drainer checks `v1MigrationInProgress(org)` before EVERY entry it would send
 *    (reviews 1–2, S-1 / SF-1) and ends its pass; one resume per migration runs a pass after it has
 *    settled, carrying the stopped passes' release flags (review 3, SF-1).
 *
 * A migration that is not running answers at once, and the wait never rejects. How long a running one
 * takes (review 2, N-a — stated exactly, it was over-claimed): acquiring its locks is BOUNDED
 * (`V1_EXCLUSIVE_WAIT_MS`, 90 s, then `busy`); the copy after that is NOT bounded — at most 100 appends
 * and one v1 save, which cannot be abandoned safely mid-way — and a watchdog pages if the migration has
 * not ended after `V1_MIGRATION_WATCHDOG_MS` (150 s, `writeCutoverSession.ts`). A migration that fails
 * or gives up releases the barrier like one that succeeded: its v1 entries then reach v2 later through
 * the 409 conversion, at the tail, where §4 puts a stray mirror anyway — and that is counted.
 *
 * In-memory and per JS context. Other web tabs are held off by the v2 drain Web Lock, which the
 * migration takes for its whole copy and v1 removal (`v1Migration.ts`, review 2 SF-1); a live append
 * in another tab is not held, and lands where it lands — counted (§16).
 */

const running = new Map<string, Promise<void>>()

/**
 * Register a migration for `orgFirestoreId`, synchronously. Returns the function that ends it (call it
 * exactly once, in a `finally`). A second registration while one runs chains behind the first.
 */
export function beginV1Migration(orgFirestoreId: string): () => void {
  let end: () => void = () => undefined
  const ended = new Promise<void>((resolve) => {
    end = resolve
  })
  const previous = running.get(orgFirestoreId) ?? Promise.resolve()
  const settled = Promise.all([previous, ended]).then(() => undefined)
  running.set(orgFirestoreId, settled)
  void settled.then(() => {
    if (running.get(orgFirestoreId) === settled) running.delete(orgFirestoreId)
  })
  return end
}

/** Resolves once no migration for this organization is running in this JS context. Never rejects. */
export function v1MigrationSettled(orgFirestoreId: string): Promise<void> {
  return running.get(orgFirestoreId) ?? Promise.resolve()
}

/**
 * The running migration's settlement, or `null` when none runs — synchronously, so a caller that has
 * nothing to wait for (every session today) does not even take an extra microtask. The session's v2
 * drainer waits on it before each pass (P8 review 1, S-1): a migrated copy must not be DELIVERED while
 * its v1 original is still queued (the copy is removed from v1 only once, at the end).
 */
export function v1MigrationInProgress(orgFirestoreId: string): Promise<void> | null {
  return running.get(orgFirestoreId) ?? null
}

/** Whether a migration for this organization is registered (diagnostics and tests). */
export function v1MigrationRunning(orgFirestoreId: string): boolean {
  return running.has(orgFirestoreId)
}

/** Test seam: a new JS context. */
export function resetV1MigrationBarrierForTests(): void {
  running.clear()
}
